/**
 * Thứ tự hiển thị của bảng đơn ở màn hình máy trạm.
 *
 * Bảng này là bảng "hoạt động", không phải bảng "đơn mới tạo". Loa đọc tại
 * thời điểm SỰ KIỆN xảy ra (duyệt đơn, soạn xong, nhận tiền), trong khi
 * `created_at` là lúc đơn được TẠO — hai mốc này cách nhau hàng giờ. Nếu xếp
 * thuần theo `created_at`, đơn vừa được đọc loa vẫn nằm im giữa danh sách và
 * nhân viên không tìm ra nó.
 *
 * Nên khoá sắp xếp là "mốc hoạt động gần nhất" = muộn hơn giữa giờ tạo đơn và
 * sự kiện cuối cùng quan sát được qua SSE.
 */

/**
 * Số đơn tối đa còn giữ mốc sự kiện. Chỉ khoảng 200 đơn hiển thị trên bảng,
 * nên ngần này là thừa đủ; giới hạn tồn tại để map không phình vô hạn trên
 * tablet chạy liên tục cả ngày.
 */
export const MAX_TRACKED_STATION_EVENTS = 300

export type StationEventTimes = ReadonlyMap<string, number>

interface SortableOrder {
    id: string
    created_at: string
}

/** Đổi mốc thời gian ISO sang epoch ms; dữ liệu hỏng coi như không có. */
function toEpoch(value: string | undefined): number {
    if (!value) return 0
    const ms = Date.parse(value)
    return Number.isNaN(ms) ? 0 : ms
}

/**
 * Ghi nhận mốc sự kiện mới nhất của một đơn.
 *
 * Trả về đúng tham chiếu cũ khi không có gì thay đổi để `setState` của React
 * bỏ qua được lần render thừa.
 */
export function rememberEventTime(
    prev: Map<string, number>,
    orderId: string | undefined,
    occurredAt: string | undefined,
    now: number,
): Map<string, number> {
    if (!orderId) return prev

    // `occurred_at` do server gửi nên cùng hệ quy chiếu với `created_at`; mốc
    // client chỉ là phương án dự phòng khi event thiếu trường này.
    const at = toEpoch(occurredAt) || now

    const next = new Map(prev)
    // Xoá rồi set lại để Map xếp key theo độ mới — nhờ đó phần cắt bớt bên
    // dưới luôn loại đúng đơn lâu nhất không có sự kiện.
    next.delete(orderId)
    next.set(orderId, at)

    while (next.size > MAX_TRACKED_STATION_EVENTS) {
        const oldest = next.keys().next().value
        if (oldest === undefined) break
        next.delete(oldest)
    }

    return next
}

/**
 * Mốc hoạt động gần nhất của một đơn (epoch ms).
 *
 * Cũng là giá trị hiển thị ở cột "Giờ": bảng xếp theo mốc này nên cột giờ phải
 * đọc cùng một mốc, nếu không thứ tự nhìn như bị hỏng.
 */
export function orderActivityTime(
    order: SortableOrder,
    eventTimes: StationEventTimes,
): number {
    return Math.max(toEpoch(order.created_at), eventTimes.get(order.id) ?? 0)
}

/**
 * Sắp xếp đơn theo hoạt động gần nhất, mới nhất lên đầu.
 *
 * Có khử trùng theo `id`: bảng ghép hai query độc lập (`confirmed` và
 * `stock_out`) nên lúc một đơn đổi trạng thái, hai query refetch lệch nhau và
 * đơn lọt vào cả hai danh sách. Bản xuất hiện SAU thắng vì thứ tự ghép là
 * [confirmed, stock_out] — tức bản sau mang trạng thái mới hơn.
 */
export function sortOrdersByActivity<T extends SortableOrder>(
    orders: readonly T[],
    eventTimes: StationEventTimes,
): T[] {
    const unique = new Map<string, T>()
    for (const order of orders) unique.set(order.id, order)

    return [...unique.values()].sort(
        (a, b) =>
            orderActivityTime(b, eventTimes) - orderActivityTime(a, eventTimes),
    )
}

/** Chuyển map mốc sự kiện thành chuỗi để lưu trữ. */
export function serializeEventTimes(times: StationEventTimes): string {
    return JSON.stringify([...times])
}

/**
 * Đọc lại map mốc sự kiện đã lưu.
 *
 * Dữ liệu này nằm ở localStorage — người dùng sửa được, phiên bản cũ có thể ghi
 * định dạng khác — nên coi như dữ liệu không đáng tin: sai định dạng thì bỏ
 * sạch, sai từng bản ghi thì bỏ riêng bản ghi đó.
 */
export function parseEventTimes(raw: string | null): Map<string, number> {
    if (!raw) return new Map()

    let decoded: unknown
    try {
        decoded = JSON.parse(raw)
    } catch {
        return new Map()
    }
    if (!Array.isArray(decoded)) return new Map()

    const times = new Map<string, number>()
    for (const entry of decoded) {
        if (!Array.isArray(entry) || entry.length !== 2) continue
        const [id, at] = entry as [unknown, unknown]
        if (typeof id !== 'string' || !id) continue
        if (typeof at !== 'number' || !Number.isFinite(at)) continue
        times.set(id, at)
    }

    // Bản ghi cuối là mới nhất, nên cắt từ đầu khi vượt giới hạn.
    while (times.size > MAX_TRACKED_STATION_EVENTS) {
        const oldest = times.keys().next().value
        if (oldest === undefined) break
        times.delete(oldest)
    }

    return times
}
